import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DataOptions } from "vuetify";
import {
  BankAccount,
  BankAccountResponse,
  BankAccountAddEdit,
} from "@/models/api/administration/bankAccounts/BankAccountsList";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import {
  getBankAccountList,
  getBankAccountById,
  getBankNames,
  getBankStatementParsers,
  getInformationAboutBankAccount,
  removeBankAccount,
  lockBankAccount,
  unlockBankAccount,
  updateBankAccount,
  addNewBankAccount,
  containsContractor,
  assignBankAccountFactor,
  removeBankAccountFactor,
} from "@/requests/bankAccounts";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { BankStatementParser } from "@/models/api/administration/bankAccounts/BankStatementParser";
import { InformationAboutBankAccount } from "@/models/api/administration/bankAccounts/InformationAboutBankAccount";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { bold } from "@/filters/bold";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
export interface AdministrationBankAccountsStateInterface {
  //Bank Accounts
  bankAccountsFilters: null | Array<CriterionBase>;
  bankAccountsCount: null | number;
  bankAccountsDataTableOptions: DataOptions;
  bankAccounts: Array<BankAccount>;
  selectedBankAccount: BankAccount;
  selectedBankAccountToEdit: BankAccountAddEdit;
  selectedBankAccountId: number;
  bankNames: Array<string>;
  newBankAccount: BankAccountAddEdit;
  allowToUpdate: boolean;

  //Parsers
  bankStatementParsers: Array<BankStatementParser>;
  informationAboutBankAccount: InformationAboutBankAccount;

  //Dialog
  isInformationDialog: boolean;
  isAddNewBankAccountDialog: boolean;
  isLoading: boolean;
}

export const AdministrationBankAccountsState: AdministrationBankAccountsStateInterface = {
  bankAccountsFilters: null,
  bankAccountsCount: -1,
  bankAccountsDataTableOptions: new DefaultDataOptions(),
  bankAccounts: [],
  selectedBankAccount: new BankAccount(),
  selectedBankAccountToEdit: new BankAccountAddEdit(),
  selectedBankAccountId: -1,
  bankNames: [],
  newBankAccount: new BankAccountAddEdit(),
  allowToUpdate: false,

  //
  bankStatementParsers: [],
  informationAboutBankAccount: new InformationAboutBankAccount(),

  //Dialog
  isInformationDialog: false,
  isAddNewBankAccountDialog: false,
  isLoading: false,
};

const AdministrationBankAccountsModule = defineModule({
  state: AdministrationBankAccountsState as AdministrationBankAccountsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    //BankAccounts
    setBankAccountFilters(state, param: Array<CriterionBase>) {
      state.bankAccountsFilters = param;
    },
    setBankAccountsDataTableOptions(state, param: DataOptions) {
      state.bankAccountsDataTableOptions = param;
    },
    setBankAccounts(state, param: BankAccountResponse) {
      state.bankAccounts = param.items;
      state.bankAccountsCount = param.totalCount;
    },
    setSelectedItemId(state, param: number) {
      state.selectedBankAccountId = param;
    },
    setSelectedItem(state, param: BankAccount) {
      state.selectedBankAccount = param;
      state.selectedBankAccountToEdit = new BankAccountAddEdit(param);
    },
    setBankNames(state, param: Array<string>) {
      state.bankNames = param;
    },
    setBankStatementParsers(state, param: Array<BankStatementParser>) {
      state.bankStatementParsers = param;
    },
    setInformationAboutBankAccount(state, param: InformationAboutBankAccount) {
      state.informationAboutBankAccount = param;
    },
    setIsInformationDialog(state, param: boolean) {
      state.isInformationDialog = param;
    },
    setIsAddNewDialog(state, param: boolean) {
      state.isAddNewBankAccountDialog = param;
    },
    setAllowToUpdate(state, param: boolean) {
      state.allowToUpdate = param;
    },
    clearBankAccount(state) {
      state.newBankAccount = new BankAccountAddEdit();
    },
  },
  actions: {
    //BankAccounts
    async downloadBankAccountsPaged(context) {
      const { commit, state } = _moduleActionContext(context);
      const bankAccounts = await getBankAccountList({
        count: state.bankAccountsDataTableOptions.itemsPerPage,
        page: state.bankAccountsDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.bankAccountsFilters),
        filterName: FILTER_NAMES.BankAccountFilter,
      });
      commit.setBankAccounts(bankAccounts);
      const items = bankAccounts.items.filter((v) => v.id == state.selectedBankAccountId);
      if (items.length > 0) {
        commit.setSelectedItemId(items[0].id);
      } else {
        commit.setSelectedItemId(bankAccounts.items[0].id);
      }
    },
    async getBankAccountById(context, bankAccountId: number) {
      const { commit } = _moduleActionContext(context);
      const bankAccount = await getBankAccountById(bankAccountId);
      commit.setSelectedItem(bankAccount);
    },
    async getBankNames(context) {
      const { commit } = _moduleActionContext(context);
      const bankNames = await getBankNames();
      commit.setBankNames(bankNames);
    },
    async getBankStatementParsers(context) {
      const { commit } = _moduleActionContext(context);
      const bankStatementParsers = await getBankStatementParsers();
      commit.setBankStatementParsers(bankStatementParsers);
    },
    async getInformationAboutBankAccount(context, bankAccountId: number) {
      const { commit } = _moduleActionContext(context);
      const info = await getInformationAboutBankAccount(bankAccountId);
      commit.setInformationAboutBankAccount(info);
    },
    async removeBankAccount(context, bankAccount: BankAccount) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      if (!bankAccount.isLocked) {
        await rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("firstLockBankAccount")), "error")
        );
      } else {
        const isRemoveConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("doYouReallyWantToDoActionBankAccount", {
                action: bold(i18n.t("toRemove")),
                bankName: bold(bankAccount.bankName),
                number: bold(bankAccount.accountNumber),
              })
            ),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isRemoveConfirmed) {
          await removeBankAccount(bankAccount.id);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.downloadBankAccountsPaged();
        }
      }
    },
    async lockBankAccount(context, bankAccount: BankAccount) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);

      const contains = await containsContractor(bankAccount.id);
      if (contains) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("doYouReallyWantToUnsetContractor", {
                action: bold(i18n.t("toLock")),
                bankName: bold(bankAccount.bankName),
                number: bold(bankAccount.accountNumber),
              })
            ),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          await lockBankAccount(bankAccount.id);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.downloadBankAccountsPaged();
        }
      } else {
        const isLockConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("doYouReallyWantToDoActionBankAccount", {
                action: bold(i18n.t("toLock")),
                bankName: bold(bankAccount.bankName),
                number: bold(bankAccount.accountNumber),
              })
            ),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isLockConfirmed) {
          await lockBankAccount(bankAccount.id);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.downloadBankAccountsPaged();
        }
      }
    },
    async unlockBankAccount(context, bankAccount: BankAccount) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isUnlockConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToDoActionBankAccount", {
              action: bold(i18n.t("toUnlock")),
              bankName: bold(bankAccount.bankName),
              number: bold(bankAccount.accountNumber),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isUnlockConfirmed) {
        await unlockBankAccount(bankAccount.id);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadBankAccountsPaged();
      }
    },
    async updateBankAccount(context) {
      const { rootDispatch, state, dispatch } = _moduleActionContext(context);
      const bankAccountUpdate = state.selectedBankAccountToEdit;
      if (bankAccountUpdate != null) {
        const isUnlockConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("doYouReallyWantToDoActionBankAccount", {
                action: bold(i18n.t("toUpdate")),
                bankName: bold(bankAccountUpdate.bankName),
                number: bold(bankAccountUpdate.accountNumber ?? ""),
              })
            ),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isUnlockConfirmed) {
          await updateBankAccount(bankAccountUpdate);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.downloadBankAccountsPaged();
        }
      }
    },
    async addNewBankAccount(context) {
      const { rootDispatch, state, dispatch, commit } = _moduleActionContext(context);
      const modelNew = state.newBankAccount;
      await addNewBankAccount(modelNew);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      commit.setIsAddNewDialog(false);
      commit.clearBankAccount();
      dispatch.downloadBankAccountsPaged();
    },
    async changeBankAccountFactor(context, params: { bankAccountId: number; contractorId: string }) {
      const { dispatch } = _moduleActionContext(context);
      await assignBankAccountFactor(params.bankAccountId, params.contractorId);
      dispatch.downloadBankAccountsPaged();
    },
    async removeBankAccountFactor(context, bankAccountId: number) {
      const { dispatch } = _moduleActionContext(context);
      await removeBankAccountFactor(bankAccountId);
      dispatch.downloadBankAccountsPaged();
    },
  },
});

export default AdministrationBankAccountsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationBankAccountsModule);
